import { useEffect, useRef, useState } from 'react';

/**
 * Reads a connected game controller via the Gamepad API (all buttons + analog
 * sticks). REQUIRES the controller to be in gamepad/HID mode — in keyboard/media
 * mode it presents as a BT keyboard and won't appear here (use the keymap instead).
 *
 * The polling loop drives control through the callbacks (no re-render); a throttled
 * snapshot is exposed only for the on-screen diagnostic.
 */
export interface GamepadSnapshot {
  connected: boolean;
  id: string;
  pressed: number[]; // indices of currently-pressed buttons
  axes: number[]; // -1..1 per axis
}

const EMPTY: GamepadSnapshot = { connected: false, id: '', pressed: [], axes: [] };

export function useGamepad(
  onButtonDown: (index: number) => void,
  onAxes: (axes: number[]) => void,
): GamepadSnapshot {
  const [snap, setSnap] = useState<GamepadSnapshot>(EMPTY);
  const prev = useRef<boolean[]>([]);
  const lastSnapAt = useRef(0);
  const downRef = useRef(onButtonDown);
  const axesRef = useRef(onAxes);
  downRef.current = onButtonDown;
  axesRef.current = onAxes;

  useEffect(() => {
    let raf = 0;
    const poll = (ts: number) => {
      const pads = navigator.getGamepads ? navigator.getGamepads() : [];
      const gp = Array.from(pads).find((p): p is Gamepad => p != null) ?? null;

      if (gp) {
        const pressedBool = gp.buttons.map((b) => b.pressed || b.value > 0.5);
        pressedBool.forEach((isDown, i) => {
          if (isDown && !prev.current[i]) downRef.current(i); // rising edge
        });
        prev.current = pressedBool;
        axesRef.current(gp.axes.slice());

        // Throttle the diagnostic snapshot to ~8fps so we don't thrash React.
        if (ts - lastSnapAt.current > 120) {
          lastSnapAt.current = ts;
          setSnap({
            connected: true,
            id: gp.id,
            pressed: pressedBool.flatMap((d, i) => (d ? [i] : [])),
            axes: gp.axes.map((a) => Math.round(a * 100) / 100),
          });
        }
      } else if (prev.current.length) {
        prev.current = [];
        setSnap(EMPTY);
      }
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, []);

  return snap;
}
